import {inject, Injectable} from '@angular/core';
import {CellRange, CellRangeEventArgs, ClipStringOptions, FlexGrid} from "@mescius/wijmo.grid";
import {FlexGridContextMenuInfo, MenuItem, MenuUtils} from '../../lib/wjmenu';
import {Menu} from '@mescius/wijmo.input';
import {AccountData, AccountDataStatusEnum, Portfolio} from '../../../api-client-kt/src';
import {CollectionView} from '@mescius/wijmo';
import {NgDynamicComponentsService} from '../../service/ng-dynamic-components.service';
import {AccountHistoryGridComponent} from '../../components/account-history-grid/account-history-grid.component';
import {Alerts, DialogButton} from '../../lib/alerts';
import {MonacoEditorComponent} from '../../components/monaco-editor/monaco-editor.component';
import {StringUtils} from '../../lib/strings';
import {PortfolioSegmentDataContext} from '../../data/portfolio-segment-data-context.service';
import {AccountGridContainerComponent} from './account-grid-ctxmenu.service';
import {AccountDataWithChecks} from '../../data/app-data-checks';
import {WjGridChangeTracker} from '../../lib/wjgrid-change-tracker';
import {AccessControlService} from '../../security/access-control.service';
import {SegmentWithPermissions} from '../../data/portfolio-data-context.service';
import {Stdlib} from '../../lib/stdlib';
import {JsonHelper} from '../../lib/json';
import {WjGridUtils} from '../../lib/wjgrid-utils';

type RowMenuCommand = 'nop' | 'row-delete' | 'row-add' | 'open-pricing-expiring' | 'open-pricing-renewal'
    | 'open-agora-expiring' | 'open-agora-renewal' | 'show-change-history' | 'comment-add' | 'show-as-json'
    | 'set-status-open' | 'set-status-completed' | 'set-status-review'
    | 'copy-data' | 'copy-data-and-headers' | 'copy-csv';

const menuItems: MenuItem<RowMenuCommand>[] = [
    { header: 'Open AGORA (Expiring)', iconCls: 'far fa-scroll-old', cmd: 'open-agora-expiring' },
    { header: 'Open AGORA (Renewal)', iconCls: 'far fa-calculator', cmd: 'open-agora-renewal' },
    { header: '-', },
    { header: 'Open Pricing (Expiring)', iconCls: 'far fa-scroll-old', cmd: 'open-pricing-expiring' },
    { header: 'Open Pricing (Renewal)', iconCls: 'far fa-calculator', cmd: 'open-pricing-renewal' },
    { header: '-', },
    { header: 'Account Status', iconCls: 'far fa-clipboard-check', cmd: 'nop', items: [
            { header: 'Mark as open', iconCls: 'far fa-clipboard-list', cmd: 'set-status-open' },
            { header: 'Mark as completed', iconCls: 'far fa-clipboard-check', cmd: 'set-status-completed' },
            { header: 'Mark for review', iconCls: 'far fa-message-question', cmd: 'set-status-review' },
        ]
    },
    { header: '-', },
    { header: 'Delete Row', iconCls: 'far fa-trash-alt', cmd: 'row-delete' },
    { header: 'Append Row', iconCls: 'far fa-plus-circle', cmd: 'row-add' },
    { header: '-', },
    { header: 'Add Comment', iconCls: 'far fa-comment-plus', cmd: 'comment-add' },
    { header: '-', },
    { header: 'Show Change History', iconCls: 'far fa-timeline-arrow', cmd: 'show-change-history' },
    { header: '-', },
    { header: 'Copy to Clipboard', iconCls: 'far fa-clipboard', cmd: 'nop',
        items: [
            { header: 'Copy Data', iconCls: 'far fa-copy', cmd: 'copy-data' },
            { header: 'Copy Data & Headers', iconCls: 'far fa-clone-plus', cmd: 'copy-data-and-headers' },
            { header: 'Copy CSV', iconCls: 'far fa-file-csv', cmd: 'copy-csv' },
        ]
    },
    { header: 'Coding', iconCls: 'far fa-code', cmd: 'nop',
        items: [
            { header: 'Show JSON', iconCls: 'far fa-file-brackets-curly', cmd: 'show-as-json' },
        ]
    }
];

class AccountRowMenuContext {

    constructor(
        public readonly menu: Menu,
        public readonly accountGridComponent: AccountGridContainerComponent
    ) {
    }

    get grid(): FlexGrid {
        return this.accountGridComponent.getGrid();
    }

    get pfSegDataCtx(): PortfolioSegmentDataContext {
        return this.accountGridComponent.getPfSegData();
    }

    get changeTracker(): WjGridChangeTracker {
        return this.accountGridComponent.getChangeTracker();
    }

    get segment(): SegmentWithPermissions {
        return this.pfSegDataCtx.activeSegment.value()!;
    }

    get portfolio(): Portfolio {
        return this.pfSegDataCtx.portfolio.value()!;
    }
}

@Injectable({
    providedIn: 'root'
})
export class AccountGridRowCtxMenuService {

    private readonly accessControlSvc = inject(AccessControlService);
    private readonly dynCompSvc = inject(NgDynamicComponentsService);

    constructor() { }

    createMenu(accountGrid: AccountGridContainerComponent): MenuItem<RowMenuCommand> {
        const menu = MenuUtils.createMenu(menuItems);
        const ctx = new AccountRowMenuContext(menu, accountGrid);
        MenuUtils.addRowContextMenu(ctx.grid, menu);
        menu.command = {
            executeCommand: this.onRowCommand.bind(this, ctx),
            canExecuteCommand: this.canExecuteRowCommand.bind(this, ctx),
        };
        return menu;
    }

    private async onRowCommand(ctx: AccountRowMenuContext, menuItem: MenuItem<RowMenuCommand>) {
        const ctxInfo = MenuUtils.getContextMenuInfo<AccountData>(ctx.menu);
        switch (menuItem.cmd) {
            case 'row-delete': await this.deleteRows(ctxInfo); break;
            case 'row-add': await this.addRows(ctxInfo); break;
            case 'comment-add': await this.addComment(ctx, ctxInfo); break;
            case 'open-pricing-expiring': await this.openPricing(ctxInfo, false); break;
            case 'open-pricing-renewal': await this.openPricing(ctxInfo, true); break;
            case 'open-agora-expiring': await this.openAgora(ctxInfo, false); break;
            case 'open-agora-renewal': await this.openAgora(ctxInfo, true); break;
            case 'show-change-history': await this.showChangeHistory(ctx, ctxInfo); break;
            case 'show-as-json': await this.showAsJson(ctxInfo); break;
            case 'set-status-open': await this.updateAccountStatus(AccountDataStatusEnum.Open, ctxInfo, ctx.changeTracker); break;
            case 'set-status-completed': await this.updateAccountStatus(AccountDataStatusEnum.Completed, ctxInfo, ctx.changeTracker); break;
            case 'set-status-review': await this.updateAccountStatus(AccountDataStatusEnum.NeedsReview, ctxInfo, ctx.changeTracker); break;
            case 'copy-data': await this.copyToClipboard(ctxInfo, false); break;
            case 'copy-data-and-headers': await this.copyToClipboard(ctxInfo, true); break;
            case 'copy-csv': await this.copyCsvClipboard(ctxInfo); break;
        }
    }

    private canExecuteRowCommand(ctx: AccountRowMenuContext, menuItem: MenuItem<RowMenuCommand>): boolean {
        const ctxInfo = MenuUtils.getContextMenuInfo(ctx.menu);
        const segment = ctx.pfSegDataCtx.activeSegment.value()!;
        const portfolio = ctx.portfolio;
        const clickedAccount = ctxInfo.clickedItem as AccountData;
        const accounts = ctxInfo.grid.selectedItems as AccountData[];
        const accountStatus = clickedAccount.status;
        const isMultipleAccounts = accounts.length > 1;

        const isSegmentEditable = this.accessControlSvc.canEditSegment(segment, ctx.portfolio).allowed;
        const areAllAccountsEditable = accounts.every(a => this.accessControlSvc.canEditAccount(a, ctx.segment, ctx.portfolio).allowed);
        const areAllAccountStatusEditable = accounts.every(a => this.accessControlSvc.canEditAccountStatus(a, ctx.segment, ctx.portfolio).allowed);

        switch (menuItem.header) {
            case 'Coding': return this.accessControlSvc.isSuperuser();
        }

        switch (menuItem.cmd) {
            case 'row-delete': return areAllAccountsEditable;
            case 'row-add': return isSegmentEditable;
            case 'comment-add': return !isMultipleAccounts && this.accessControlSvc.canAddComment(segment, clickedAccount);
            case 'open-pricing-expiring': return !ctxInfo.isMultiSelect && !StringUtils.isNullOrEmpty(clickedAccount.crmSubmissionId);
            case 'open-pricing-renewal': return !ctxInfo.isMultiSelect && !StringUtils.isNullOrEmpty(clickedAccount.crmRenewalId);
            case 'open-agora-expiring':  return !ctxInfo.isMultiSelect && !StringUtils.isNullOrEmpty(clickedAccount.expiringSubmissionUuid);
            case 'open-agora-renewal':  return !ctxInfo.isMultiSelect && !StringUtils.isNullOrEmpty(clickedAccount.renewalSubmissionUuid);
            case 'set-status-open': return areAllAccountStatusEditable && accountStatus !== AccountDataStatusEnum.Open;
            case 'set-status-completed': return areAllAccountStatusEditable && accountStatus !== AccountDataStatusEnum.Completed;
            case 'set-status-review': return areAllAccountStatusEditable && accountStatus !== AccountDataStatusEnum.NeedsReview;
            case 'show-change-history': return !isMultipleAccounts;

            default: return true;
        }
    }

    private async updateAccountStatus(status: AccountDataStatusEnum, ctxInfo: FlexGridContextMenuInfo<AccountData>, changeTracker: WjGridChangeTracker) {
        const accounts = ctxInfo.grid.selectedItems as AccountData[];
        const cv = ctxInfo.grid.itemsSource as CollectionView<AccountData>;
        cv.deferUpdate(() => {
            accounts.forEach((a) => {
                cv.editItem(a);
                const oldStatus = a.status;
                a.status = status;
                cv.commitEdit();
                changeTracker.notifyCellItemChange(a, 'status', oldStatus, status);
            });
        });

        // and sometimes, the grid does not refresh immediately, so we force a refresh after a short delay
        setTimeout(() => {
            ctxInfo.grid.invalidate(true);
            ctxInfo.grid.refresh(true);
        }, 1_000);
    }

    private async deleteRows(ctxInfo: FlexGridContextMenuInfo<AccountData>) {
        const grid = ctxInfo.grid;
        const row1 = Math.min(grid.selection.row, grid.selection.row2);
        const row2 = Math.max(grid.selection.row, grid.selection.row2);
        const numRows = row2 - row1 + 1;

        const btn = await Alerts.alertPopup({
            header: 'Delete Rows',
            bodyHtml: `You have selected ${numRows} rows for deletion. Would you like to proceed?`,
            buttons: [DialogButton.Cancel, DialogButton.Yes],
            height: '150px',
            width: '350px',
        });
        if (btn === DialogButton.Cancel) return;

        const cv = grid.itemsSource as CollectionView<AccountData>;

        // notify the grid about the rows being deleted, because the UndoStack
        // is listening to these single row deletion events
        for (let i = row1; i <= row2; i++) {
            grid.onDeletingRow(new CellRangeEventArgs(grid.cells, new CellRange(i, grid.selection.col, i, grid.selection.col2)));
            grid.onDeletedRow(new CellRangeEventArgs(grid.cells, new CellRange(i, grid.selection.col, i, grid.selection.col2)));
        }

        // finally, remove one-by-one as there is no bulk delete
        ctxInfo.selectedItems.forEach(item => cv.remove(item));
    }

    private async addRows(ctxInfo: FlexGridContextMenuInfo<AccountData>) {
        const num = await Alerts.alertPromptNumeric({
            body: 'Enter the number of rows to add',
            header: 'Add New Rows',
            initialValue: 1
        });
        if (num && num > 0) {
            const grid = ctxInfo.grid;
            const cv = grid.itemsSource as CollectionView<AccountData>;
            for (let i = 0; i < num; i++) {
                const newRowNum = cv.sourceCollection.length + i;
                cv.addNew({id: StringUtils.newUuid7(), row: newRowNum} as any);

                // notify the grid about the new row being added, because of the UndoStack
                // and commit afterward, which adds the event to the UndoStack
                grid.onRowAdded(new CellRangeEventArgs(grid.cells, grid.selection));
                cv.commitNew();
            }
        }
    }

    private async openAgora(ctxInfo: FlexGridContextMenuInfo<AccountData>, renewal: boolean) {
        const acct = ctxInfo.clickedItem;
        const submissionId = renewal ? acct.renewalSubmissionUuid : acct.expiringSubmissionUuid;

        // build query params safely and encode them
        const params = new URLSearchParams();
        params.set('appid', '9590bc98-e7a6-4ae9-8287-006604e6119c');
        params.set('forceUCI', '1');
        params.set('pagetype', 'entityrecord');
        params.set('etn', 'gen_submission');
        params.set('id', submissionId!);

        const pricingUri = `https://agora-prod.crm4.dynamics.com/main.aspx?${params.toString()}`;
        window.open(pricingUri, '_blank');
    }

    private async openPricing(ctxInfo: FlexGridContextMenuInfo<AccountData>, renewal: boolean) {
        const acct = ctxInfo.clickedItem;
        const popup = Alerts.showProgressDialog('Checking for Pricing Tool...');
        try {
            const postRequest = fetch('https://pricing.generali.co.uk/gpt-service/ui/v1/pricingdb/search', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Api-Key': 'e7a9e0fe-19a4-4cc3-8561-2553efd56284',
                },
                body: JSON.stringify({
                    type: "Pricing",
                    guns:"",
                    accountKey: "", //acct.uwbId,
                    submissionId: renewal ? acct.crmRenewalId : acct.crmSubmissionId,
                    sic:"",
                    countries:[],
                    createdAtMin:null,
                    inceptionMin:"",
                    inceptionMax:"",
                    // tool:"Property",
                    toolVersion:"",
                    uwy:[],
                    premiumMin:null,
                    premiumMax:null,
                    aptpMin:null,
                    aptpMax:null,
                    ercMin:null,
                    ercMax:null,
                    createdBy:[],
                    tags:[],
                    status:[],
                    duplicates:"selected",
                    taskGroupId: null
                })
            });

            const response = await postRequest;
            if (!response.ok) {
                throw new Error(`Failed to fetch pricing URI: ${response.statusText}`);
            }
            const data = await response.json() as { resultdataref: string | null }[];
            if (!data || data.length === 0 || !data[0].resultdataref) {
                throw new Error('No pricing data found for the account');
            }

            Alerts.alertPopup({
                body: `
                    Please note, that this is only a read-only view.
                    To modify the Pricing data, please open the Pricing tool via the AGORA link.
                `,
                buttons: [DialogButton.Ok],
                onPopupCreated: async (popup) => {
                    await Stdlib.sleep(2000);
                    popup.hide();
                    const pricingUri =  `https://pricing.generali.co.uk/gpt-service/dart/v1/files/${data[0].resultdataref}/view?viewMode=view`;
                    window.open(pricingUri, '_blank');
                }
            });
        } catch (error: any) {
            await Alerts.alertPopup({ body: `Failed to open Pricing Tool: ${error?.message ?? error.toString()}` });
            // alert(error);
        } finally {
            popup.hide();
        }
    }

    private async showChangeHistory(ctx: AccountRowMenuContext, ctxInfo: FlexGridContextMenuInfo<AccountData>) {
        const account = ctxInfo.clickedItem;
        await this.dynCompSvc.createComponentDialog(AccountHistoryGridComponent, {
            title: `Change History for Account: ${account.account} (${account.uwbId ?? account.crmSubmissionId})`,
            width: '80%',
            height: '400px',
            input: { accountId: account.id, pfSegDataCtx: ctx.pfSegDataCtx }
        });
    }

    private async addComment(ctx: AccountRowMenuContext, ctxInfo: FlexGridContextMenuInfo<AccountData>) {
        const ht = ctxInfo.hitTestInfo;
        const cellElement = ctx.grid.cells.getCellElement(ht.row, ht.col);
        const item = ht.getRow()?.dataItem as AccountDataWithChecks;
        const column = ctx.grid.columns.getColumn(ht.col);
        const binding = column?.binding as string;
        await ctx.accountGridComponent.showCellCommentsEditor(item, binding, cellElement);
    }

    private async showAsJson(ctxInfo: FlexGridContextMenuInfo<AccountData>) {
        const account = ctxInfo.clickedItem;
        const json = JsonHelper.jsonStringifySorted(account);
        await this.dynCompSvc.createComponentDialog(MonacoEditorComponent, {
            title: 'Account JSON',
            width: 'min(80vw, 1000px)',
            height: 'max(80vh, 600px)',
            input: { initialValue: json, language: 'json' },
        });
    }

    private async copyToClipboard(ctxInfo: FlexGridContextMenuInfo<AccountData>, includeHeaders: boolean) {
        WjGridUtils.copyRangeToClipboard(ctxInfo.grid, ctxInfo.grid.selection, includeHeaders);
    }

    private async copyCsvClipboard(ctxInfo: FlexGridContextMenuInfo<AccountData>) {
        WjGridUtils.copyRangeToClipboardAsCsv(ctxInfo.grid, ctxInfo.grid.selection, true);
    }
}
